<template>
  <div class="home-view">
    <img alt="Premium logo" class="premium-logo" :src="premiumLogo" width="250" height="250" />

    <h1 v-if="userRoles.includes('tier_2')">Welcome back, Premium member!</h1>
    <h1 v-else>Take full control of your finances with Premium!</h1>

    <!-- Loading spinner for subscription info -->
    <div v-if="userRoles.includes('tier_2') && isLoadingSubscription" class="subscription-loading">
      <div class="spinner"></div>
      <p>Loading subscription details...</p>
    </div>

    <SubscriptionInfo
      v-if="userRoles.includes('tier_2') && subscription && !isLoadingSubscription"
      :subscription="subscription"
      @unsubscribe="handleUnsubscribe"
    />

    <button v-if="!userRoles.includes('tier_2')" @click="handleSubscribe" :disabled="isLoadingPayment" class="button subscribe-btn">
      {{ isLoadingPayment ? 'Loading...' : 'Subscribe' }}
    </button>

    <UnsubscribeModal
      :show-modal="showUnsubscribeModal"
      :is-unsubscribing="isUnsubscribing"
      @close="closeModal"
      @confirm="confirmUnsubscribe"
    />
  </div>
</template>

<script setup>
import { useAuth0 } from '@auth0/auth0-vue'
import { onMounted, ref } from 'vue'
import premiumLogo from '@/assets/premium.svg'
import { setupAuthInterceptor, reportError, createTier2PaymentSession, getSubscriptions, deleteSubscription } from '@/utils/api'
import SubscriptionInfo from '@/components/SubscriptionInfo.vue'
import UnsubscribeModal from '@/components/UnsubscribeModal.vue'

const { idTokenClaims, getAccessTokenSilently } = useAuth0()
const userRoles = ref([])
const subscription = ref(null)
const showUnsubscribeModal = ref(false)
const isLoadingPayment = ref(false)
const isUnsubscribing = ref(false)
const isLoadingSubscription = ref(false)


const handleSubscribe = async () => {
  try {
    isLoadingPayment.value = true
    const data = await createTier2PaymentSession()
    window.location.href = data
  } catch (error) {
    await reportError(error)
  } finally {
    isLoadingPayment.value = false
  }
}

const handleUnsubscribe = () => {
  showUnsubscribeModal.value = true
}

const closeModal = () => {
  showUnsubscribeModal.value = false
}

async function confirmUnsubscribe() {
  try {
    // unsubscribe
    isUnsubscribing.value = true
    showUnsubscribeModal.value = false
    await deleteSubscription()

    // poll for role change
    const startTime = Date.now()
    const maxWaitTime = 15000 // 15 seconds
    var currentRoles = idTokenClaims.value?.['https://auth.cinchbudget.com/roles'] || []
    while (currentRoles.includes('tier_2') && Date.now() - startTime < maxWaitTime) {
      await getAccessTokenSilently({ cacheMode: 'off' })
      currentRoles = idTokenClaims.value?.['https://auth.cinchbudget.com/roles'] || []
      await new Promise((resolve) => setTimeout(resolve, 250))
    }

    // handle role change or timeout
    if (currentRoles.includes('tier_2')) {
      isUnsubscribing.value = false
      alert('An unexpected error occurred. Please try again later.')
      return
    } else {
      window.location.reload()
    }
  } catch (error) {
    await reportError(error)
  } finally {
    isUnsubscribing.value = false
  }
}

onMounted(async () => {
  try {
    setupAuthInterceptor()
    if (idTokenClaims.value) {
      userRoles.value = idTokenClaims.value['https://auth.cinchbudget.com/roles'] || []

      if (userRoles.value.includes('tier_2')) {
        isLoadingSubscription.value = true
        try {
          subscription.value = await getSubscriptions()
        } finally {
          isLoadingSubscription.value = false
        }
      }
    }
  } catch (error) {
    await reportError(error)
  }
})
</script>

<style scoped>
.home-view {
  padding: 2rem;
  text-align: center;
}

@media (max-width: 450px) {
  .home-view {
    padding: 1rem;
  }
}

.premium-logo {
  display: block;
  margin: 0 auto 2rem auto;
}

@media (max-width: 450px) {
  .premium-logo {
    width: 150px;
    height: 150px;
    margin-bottom: 1rem;
  }
}

h1 {
  color: var(--color-heading);
  margin-bottom: 1rem;
  font-size: 2rem;
}

@media (max-width: 450px) {
  h1 {
    font-size: 1.5rem;
  }
}

.subscribe-btn {
  display: inline-block;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  transition: background-color 0.3s;
  margin: 2rem 0;
}

@media (max-width: 450px) {
  .subscribe-btn {
    padding: 0.85rem 1.5rem;
    font-size: 1rem;
    margin: 1.5rem 0;
  }
}

.subscription-loading {
  text-align: center;
  margin: 2rem 0;
}

.subscription-loading p {
  margin-top: 1rem;
  color: var(--color-text);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-heading);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>